import React, { useEffect, useMemo, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import MediaInput from "@/components/MediaInput";
import {
  Plus, Trash2, Pencil, ChevronUp, ChevronDown, Save, X, Eye, EyeOff,
  Star, StarOff, Clapperboard, Music,
} from "lucide-react";

/**
 * PresentationTab — Phase 4 Scenario Builder
 *
 * Sales reps can craft multiple guided sales scenarios per project (e.g.
 * "Investor pitch", "Family walkthrough", "VIP tour"). Each scenario is
 * an ordered list of steps that drive the Public Viewer: scenes, etap/
 * block/unit highlights, "open shortlist", panorama walk-throughs, etc.
 *
 * Layout:
 *   ┌─ Scenario chips (one is default ⭐) ───── + Add scenario
 *   ├─ Scenario meta (name / description / default)
 *   └─ Steps table for the selected scenario
 */
export default function PresentationTab({ pid, scenes, groups, entities }) {
  const [scenarios, setScenarios] = useState([]);
  const [steps, setSteps] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stepForm, setStepForm] = useState(null);
  const [scForm, setScForm] = useState(null); // scenario create/edit form

  const activeScenario = useMemo(
    () => scenarios.find((s) => s.id === activeId) || null,
    [scenarios, activeId]
  );

  const loadScenarios = async (preserveActive = true) => {
    const r = await api.get(`/admin/projects/${pid}/presentation-scenarios`);
    const list = r.data || [];
    setScenarios(list);
    if (!list.length) { setActiveId(null); return list; }
    const stillExists = preserveActive && activeId && list.some((x) => x.id === activeId);
    const next = stillExists
      ? activeId
      : (list.find((x) => x.is_default) || list[0]).id;
    setActiveId(next);
    return list;
  };

  const loadSteps = async (scenarioId) => {
    if (!scenarioId) { setSteps([]); return; }
    const r = await api.get(`/admin/projects/${pid}/presentation-steps`, {
      params: { scenario_id: scenarioId },
    });
    setSteps(r.data || []);
  };

  useEffect(() => {
    (async () => {
      try { await loadScenarios(false); }
      catch (e) { toast.error(apiErrorMessage(e)); }
      finally { setLoading(false); }
    })();
  }, [pid]);

  useEffect(() => { loadSteps(activeId); }, [activeId]);

  // ── Scenario actions ────────────────────────────────────────────────
  const saveScenario = async () => {
    try {
      if (!scForm.name?.trim()) { toast.error("Scenario name is required"); return; }
      if (scForm.id) {
        const { id, ...patch } = scForm;
        await api.put(`/admin/presentation-scenarios/${id}`, patch);
        toast.success("Scenario updated");
      } else {
        const r = await api.post(`/admin/presentation-scenarios`, {
          project_id: pid, ...scForm,
        });
        setActiveId(r.data.id);
        toast.success("Scenario created");
      }
      setScForm(null);
      await loadScenarios();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const toggleDefault = async (sc) => {
    try {
      await api.put(`/admin/presentation-scenarios/${sc.id}`, { is_default: !sc.is_default });
      await loadScenarios();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const deleteScenario = async (sc) => {
    if (!confirm(`Delete scenario "${sc.name}"? All its steps will be removed.`)) return;
    try {
      await api.delete(`/admin/presentation-scenarios/${sc.id}`);
      toast.success("Scenario deleted");
      await loadScenarios(false);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  // ── Step actions ────────────────────────────────────────────────────
  const emptyStep = () => ({
    project_id: pid,
    scenario_id: activeId,
    title: "",
    description: "",
    target_type: "scene",
    target_id: "",
    button_label: "Next",
    sort_order: steps.length,
    visibility: true,
    auto_advance: false,
    duration_seconds: 6,
  });

  const saveStep = async () => {
    try {
      const body = { ...stepForm };
      if (!body.title?.trim()) { toast.error("Title is required"); return; }
      if (NEEDS_TARGET_ID.has(body.target_type) && !body.target_id) {
        toast.error("Pick a target for this step"); return;
      }
      // Server allows null target_id for some types; coerce empty → null.
      if (!body.target_id) body.target_id = null;
      // Coerce duration to number; clear when auto_advance is off.
      if (!body.auto_advance) body.duration_seconds = null;
      else body.duration_seconds = Number(body.duration_seconds) || 6;
      if (body.id) await api.put(`/admin/presentation-steps/${body.id}`, body);
      else await api.post("/admin/presentation-steps", body);
      toast.success("Saved");
      setStepForm(null);
      await loadSteps(activeId);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const delStep = async (id) => {
    if (!confirm("Delete this step?")) return;
    await api.delete(`/admin/presentation-steps/${id}`);
    await loadSteps(activeId);
  };

  const moveStep = async (idx, dir) => {
    const nextIdx = idx + dir;
    if (nextIdx < 0 || nextIdx >= steps.length) return;
    const next = [...steps];
    [next[idx], next[nextIdx]] = [next[nextIdx], next[idx]];
    setSteps(next);
    try {
      await api.put(`/admin/projects/${pid}/presentation-steps/reorder`, {
        ids: next.map((s) => s.id),
        scenario_id: activeId,
      });
    } catch (e) { toast.error(apiErrorMessage(e)); loadSteps(activeId); }
  };

  const toggleVisibility = async (s) => {
    try {
      await api.put(`/admin/presentation-steps/${s.id}`, { visibility: !s.visibility });
      await loadSteps(activeId);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div className="max-w-5xl" data-testid="presentation-tab">
      <div className="mb-3">
        <h2 className="text-lg font-medium">Presentation Mode · Scenario Builder</h2>
        <p className="text-sm text-zinc-500 mt-1">
          Build guided sales sequences. Each scenario is a curated journey through
          the project — scenes, etaps, units, plans, panoramas, even a &ldquo;show me
          the shortlist&rdquo; beat — all driven by Next/Prev in the Public Viewer.
        </p>
      </div>

      {/* Scenario chips */}
      <div className="rounded-xl border border-zinc-200 bg-white p-3 mb-4" data-testid="scenario-chips">
        <div className="flex flex-wrap gap-2 items-center">
          {scenarios.map((sc) => {
            const active = sc.id === activeId;
            return (
              <button
                key={sc.id}
                onClick={() => setActiveId(sc.id)}
                data-testid={`scenario-chip-${sc.id}`}
                aria-label={sc.is_default ? `Default scenario: ${sc.name}` : sc.name}
                className={`group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition ${
                  active
                    ? "bg-zinc-900 text-white"
                    : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                }`}
              >
                {sc.is_default && <Star className="h-3.5 w-3.5 fill-current" />}
                {sc.name}
              </button>
            );
          })}
          <button
            onClick={() => setScForm({ name: "", description: "", is_default: scenarios.length === 0 })}
            data-testid="add-scenario-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border border-dashed border-zinc-300 hover:bg-zinc-50"
          >
            <Plus className="h-3.5 w-3.5" /> New scenario
          </button>
        </div>

        {activeScenario && (
          <div className="mt-3 flex items-center gap-2 text-xs text-zinc-500" data-testid="scenario-meta">
            {activeScenario.description && <span className="italic">{activeScenario.description}</span>}
            <div className="ml-auto flex items-center gap-1">
              <button onClick={() => toggleDefault(activeScenario)} className="px-2 py-1 rounded hover:bg-zinc-100 inline-flex items-center gap-1" data-testid={`scenario-toggle-default-${activeScenario.id}`}>
                {activeScenario.is_default ? <><StarOff className="h-3.5 w-3.5" />Unset default</> : <><Star className="h-3.5 w-3.5" />Set default</>}
              </button>
              <button onClick={() => setScForm({ ...activeScenario })} className="px-2 py-1 rounded hover:bg-zinc-100 inline-flex items-center gap-1" data-testid={`scenario-edit-${activeScenario.id}`}>
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
              <button onClick={() => deleteScenario(activeScenario)} className="px-2 py-1 rounded hover:bg-red-50 text-red-600 inline-flex items-center gap-1" data-testid={`scenario-delete-${activeScenario.id}`}>
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-zinc-700">
          {activeScenario ? <>Steps in <span className="font-medium">{activeScenario.name}</span></> : "Pick or create a scenario"}
        </div>
        {activeScenario && (
          <button
            onClick={() => setStepForm(emptyStep())}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-zinc-900 text-white text-sm"
            data-testid="presentation-add-step"
          >
            <Plus className="h-4 w-4" /> Add step
          </button>
        )}
      </div>

      <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50/60 text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3 w-10">#</th>
              <th className="text-left px-4 py-3">Title</th>
              <th className="text-left px-4 py-3">Target</th>
              <th className="text-left px-4 py-3">Auto-advance</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-400">Loading…</td></tr>}
            {!loading && activeScenario && steps.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500" data-testid="presentation-empty">
                No steps yet — add your first one above.
              </td></tr>
            )}
            {!loading && !activeScenario && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500">
                Create a scenario above to start building steps.
              </td></tr>
            )}
            {steps.map((s, idx) => {
              const target = describeTarget(s, { scenes, groups, entities });
              return (
                <tr key={s.id} className={`border-t border-zinc-100 ${!s.visibility ? "opacity-50" : ""}`} data-testid={`step-row-${s.id}`}>
                  <td className="px-4 py-3 text-zinc-500 font-mono">{idx + 1}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium flex items-center gap-1.5">
                      {s.title}
                      {s.audio_url && <Music className="h-3 w-3 text-[#C4A47C]" data-testid={`step-has-audio-${s.id}`} />}
                    </div>
                    {s.description && <div className="text-xs text-zinc-500">{s.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-zinc-600 text-xs">
                    <span className="font-mono uppercase mr-1.5 text-zinc-400">{s.target_type}</span> {target}
                  </td>
                  <td className="px-4 py-3 text-zinc-600 text-xs">
                    {s.auto_advance ? <><Clapperboard className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />{s.duration_seconds || 6}s</> : "—"}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => moveStep(idx, -1)} disabled={idx === 0} className="p-1.5 hover:bg-zinc-100 rounded disabled:opacity-30" data-testid={`step-up-${s.id}`}><ChevronUp className="h-4 w-4" /></button>
                    <button onClick={() => moveStep(idx, +1)} disabled={idx === steps.length - 1} className="p-1.5 hover:bg-zinc-100 rounded disabled:opacity-30" data-testid={`step-down-${s.id}`}><ChevronDown className="h-4 w-4" /></button>
                    <button onClick={() => toggleVisibility(s)} className="p-1.5 hover:bg-zinc-100 rounded" data-testid={`step-toggle-${s.id}`}>
                      {s.visibility ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </button>
                    <button onClick={() => setStepForm({ ...s })} className="p-1.5 hover:bg-zinc-100 rounded" data-testid={`step-edit-${s.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => delStep(s.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`step-delete-${s.id}`}><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {scForm && (
        <ScenarioForm form={scForm} setForm={setScForm} onCancel={() => setScForm(null)} onSave={saveScenario} />
      )}
      {stepForm && (
        <StepForm
          form={stepForm}
          setForm={setStepForm}
          scenes={scenes}
          groups={groups}
          entities={entities}
          onCancel={() => setStepForm(null)}
          onSave={saveStep}
        />
      )}
    </div>
  );
}

// ── Step target metadata ───────────────────────────────────────────────
const TARGET_TYPES = [
  { v: "scene", label: "Scene" },
  { v: "panorama_scene", label: "Panorama (360) scene" },
  { v: "etap", label: "Etap (filter units list)" },
  { v: "block", label: "Block (filter units list)" },
  { v: "unit", label: "Unit — details" },
  { v: "unit_plan", label: "Unit — floor plan" },
  { v: "unit_gallery", label: "Unit — gallery" },
  { v: "open_unit_360", label: "Unit — 360 tour" },
  { v: "highlight_etap", label: "Spotlight: Etap on map" },
  { v: "highlight_block", label: "Spotlight: Block on map" },
  { v: "highlight_unit", label: "Spotlight: Unit on plan" },
  { v: "open_units_list", label: "Open Units list (no target)" },
  { v: "open_shortlist", label: "Open Shortlist (no target)" },
  { v: "open_payment_plan", label: "Open Payment plan for unit" },
  { v: "amenity", label: "Amenity (unit-typed)" },
  { v: "location", label: "Location / Map scene" },
  { v: "info_slide", label: "Info slide (no target)" },
];

// Target types that REQUIRE a target_id to be picked.
const NEEDS_TARGET_ID = new Set([
  "scene", "panorama_scene",
  "etap", "block",
  "unit", "unit_plan", "unit_gallery", "open_unit_360", "open_payment_plan",
  "highlight_etap", "highlight_block", "highlight_unit",
  "amenity", "location",
]);

function describeTarget(step, ctx) {
  const { scenes, groups, entities } = ctx;
  if (!NEEDS_TARGET_ID.has(step.target_type)) return "—";
  if (!step.target_id) return "(none)";
  if (step.target_type === "scene" || step.target_type === "panorama_scene" || step.target_type === "location") {
    return scenes.find((s) => s.id === step.target_id)?.name || step.target_id;
  }
  if (step.target_type === "etap" || step.target_type === "block"
      || step.target_type === "highlight_etap" || step.target_type === "highlight_block") {
    return groups.find((g) => g.id === step.target_id)?.name || step.target_id;
  }
  // unit-ish
  return entities.find((e) => e.id === step.target_id)?.code || step.target_id;
}

// ── Scenario create/edit modal ─────────────────────────────────────────
function ScenarioForm({ form, setForm, onCancel, onSave }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6 fade-in" onClick={onCancel} data-testid="scenario-form-modal">
      <div className="w-full max-w-md bg-white rounded-xl shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">{form.id ? "Edit scenario" : "New scenario"}</h3>
          <button onClick={onCancel} className="text-zinc-400 hover:text-zinc-900"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs text-zinc-600 mb-1 block">Name</span>
            <input className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="scenario-name" />
          </label>
          <label className="block">
            <span className="text-xs text-zinc-600 mb-1 block">Description (optional)</span>
            <textarea className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm h-16" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="scenario-description" />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} data-testid="scenario-is-default" />
            Default scenario (auto-starts the &ldquo;▶ Start presentation&rdquo; button)
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 rounded-md text-sm border border-zinc-300 hover:bg-zinc-50">Cancel</button>
          <button onClick={onSave} className="px-4 py-2 rounded-md text-sm bg-zinc-900 text-white inline-flex items-center gap-1.5" data-testid="scenario-save">
            <Save className="h-4 w-4" /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Step create/edit modal ─────────────────────────────────────────────
function StepForm({ form, setForm, scenes, groups, entities, onCancel, onSave }) {
  const etaps = useMemo(() => groups.filter((g) => g.group_type === "etap"), [groups]);
  const blocks = useMemo(() => groups.filter((g) => g.group_type === "block"), [groups]);
  const panoramaScenes = useMemo(() => scenes.filter((s) => s.type === "panorama_360"), [scenes]);
  const nonPanorama = useMemo(() => scenes.filter((s) => s.type !== "panorama_360"), [scenes]);
  const amenityUnits = useMemo(() => entities.filter((e) => (e.type || "").toLowerCase() === "amenity"), [entities]);
  const targets = useMemo(() => {
    switch (form.target_type) {
      case "scene":
      case "location":
        return nonPanorama.map((s) => ({ id: s.id, label: s.name }));
      case "panorama_scene":
        return panoramaScenes.map((s) => ({ id: s.id, label: s.name }));
      case "etap":
      case "highlight_etap":
        return etaps.map((g) => ({ id: g.id, label: g.name }));
      case "block":
      case "highlight_block":
        return blocks.map((g) => ({ id: g.id, label: g.name }));
      case "amenity":
        return amenityUnits.map((e) => ({ id: e.id, label: e.code + (e.name && e.name !== e.code ? ` — ${e.name}` : "") }));
      case "unit":
      case "unit_plan":
      case "unit_gallery":
      case "open_unit_360":
      case "open_payment_plan":
      case "highlight_unit":
        return entities.map((e) => ({ id: e.id, label: e.code + (e.name && e.name !== e.code ? ` — ${e.name}` : "") }));
      default: return [];
    }
  }, [form.target_type, nonPanorama, panoramaScenes, etaps, blocks, entities, amenityUnits]);

  const requiresTarget = NEEDS_TARGET_ID.has(form.target_type);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6 fade-in" onClick={onCancel} data-testid="step-form-modal">
      <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">{form.id ? "Edit step" : "New step"}</h3>
          <button onClick={onCancel} className="text-zinc-400 hover:text-zinc-900"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs text-zinc-600 mb-1 block">Title</span>
            <input className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="step-title" />
          </label>
          <label className="block">
            <span className="text-xs text-zinc-600 mb-1 block">Description (optional)</span>
            <textarea className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm h-16" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="step-description" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-zinc-600 mb-1 block">Target type</span>
              <select className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" value={form.target_type} onChange={(e) => setForm({ ...form, target_type: e.target.value, target_id: "" })} data-testid="step-target-type">
                {TARGET_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-zinc-600 mb-1 block">Target</span>
              <select
                className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm disabled:bg-zinc-50"
                value={form.target_id || ""}
                onChange={(e) => setForm({ ...form, target_id: e.target.value })}
                disabled={!requiresTarget}
                data-testid="step-target-id"
              >
                <option value="">{requiresTarget ? "Select…" : "—"}</option>
                {targets.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-xs text-zinc-600 mb-1 block">Button label</span>
            <input className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" value={form.button_label || ""} onChange={(e) => setForm({ ...form, button_label: e.target.value })} placeholder="Next" data-testid="step-button-label" />
          </label>
          <div className="rounded-md border border-zinc-200 p-3 bg-zinc-50/60">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.auto_advance} onChange={(e) => setForm({ ...form, auto_advance: e.target.checked })} data-testid="step-auto-advance" />
              Auto-advance to next step
            </label>
            {form.auto_advance && (
              <div className="mt-2 flex items-center gap-2 text-xs text-zinc-600">
                <span className="w-20">Duration</span>
                <input
                  type="number" min="1" max="60" step="0.5"
                  className="flex-1 px-2 py-1 border border-zinc-300 rounded text-sm"
                  value={form.duration_seconds ?? 6}
                  onChange={(e) => setForm({ ...form, duration_seconds: e.target.value })}
                  data-testid="step-duration-seconds"
                /> sec
              </div>
            )}
          </div>

          {/* Phase 5 — presentation-only timed audio */}
          <div className="rounded-md border border-zinc-200 p-3 bg-zinc-50/60" data-testid="step-audio-section">
            <div className="flex items-center gap-1.5 text-sm font-medium text-zinc-800 mb-2">
              <Music className="h-3.5 w-3.5" /> Step audio (plays only in Presentation Mode)
            </div>
            <MediaInput
              projectId={form.project_id}
              value={form.audio_url || ""}
              onChange={(v) => setForm({ ...form, audio_url: v || null })}
              accept="audio/*"
              testid="step-audio"
            />
            <p className="text-[11px] text-zinc-500 mt-1.5">
              Crossfades in when this step opens and fades out on the next step / exit. Leave empty to keep the previous step&rsquo;s track playing.
            </p>
            {form.audio_url && (
              <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-zinc-600">
                <label className="block">
                  <span className="mb-1 block">Volume ({Math.round((form.audio_volume ?? 0.6) * 100)}%)</span>
                  <input
                    type="range" min="0" max="1" step="0.05"
                    className="w-full"
                    value={form.audio_volume ?? 0.6}
                    onChange={(e) => setForm({ ...form, audio_volume: parseFloat(e.target.value) })}
                    data-testid="step-audio-volume"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block">Fade ({form.audio_fade_seconds ?? 1.2}s)</span>
                  <input
                    type="range" min="0" max="6" step="0.1"
                    className="w-full"
                    value={form.audio_fade_seconds ?? 1.2}
                    onChange={(e) => setForm({ ...form, audio_fade_seconds: parseFloat(e.target.value) })}
                    data-testid="step-audio-fade"
                  />
                </label>
                <label className="flex items-center gap-2 col-span-2">
                  <input
                    type="checkbox"
                    checked={form.audio_loop !== false}
                    onChange={(e) => setForm({ ...form, audio_loop: e.target.checked })}
                    data-testid="step-audio-loop"
                  />
                  Loop while this step is on screen
                </label>
              </div>
            )}
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 rounded-md text-sm border border-zinc-300 hover:bg-zinc-50">Cancel</button>
          <button onClick={onSave} className="px-4 py-2 rounded-md text-sm bg-zinc-900 text-white inline-flex items-center gap-1.5" data-testid="step-save">
            <Save className="h-4 w-4" /> Save
          </button>
        </div>
      </div>
    </div>
  );
}
